
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, AuthSession, Permission } from '../types/auth';
import { ADMIN_ENABLED } from '../utils/adminAccess';

interface AuthContextType {
  currentUser: AuthSession | null;
  user: AuthSession | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => boolean;
  logout: () => void;
  hasPermission: (resource: keyof Permission, action: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEYS = {
  USERS: 'admin_users',
  SESSION: 'admin_session'
};

// Права по умолчанию для админа — полный доступ
const ADMIN_PERMISSIONS: Permission = {
  promocodes: { view: true, add: true, edit: true, delete: true },
  stores: { view: true, add: true, edit: true, delete: true },
  statistics: { view: true },
  users: { view: true, add: true, edit: true, delete: true },
  settings: { view: true, edit: true },
};

// Базовые права для менеджера (только просмотр)
const MANAGER_BASE_PERMISSIONS: Permission = {
  promocodes: { view: true, add: false, edit: false, delete: false },
  stores: { view: true, add: false, edit: false, delete: false },
  statistics: { view: true },
  users: { view: false, add: false, edit: false, delete: false },
  settings: { view: false, edit: false },
};

// Миграция старых пользователей — добавляем новые поля permissions
const migrateUserPermissions = (user: User): User => {
  const permissions = user.permissions as Partial<Permission>;

  return {
    ...user,
    permissions: {
      promocodes: permissions.promocodes ?? MANAGER_BASE_PERMISSIONS.promocodes,
      stores: permissions.stores ?? MANAGER_BASE_PERMISSIONS.stores,
      statistics: permissions.statistics ?? MANAGER_BASE_PERMISSIONS.statistics,
      users: permissions.users ?? (
        user.role === 'admin'
          ? { view: true, add: true, edit: true, delete: true }
          : { view: false, add: false, edit: false, delete: false }
      ),
      settings: permissions.settings ?? (
        user.role === 'admin'
          ? { view: true, edit: true }
          : { view: false, edit: false }
      ),
    },
  };
};

// Инициализация пользователей
const initializeUsers = (): User[] => {
  const existingUsers = localStorage.getItem(STORAGE_KEYS.USERS);
  if (existingUsers) {
    const users: User[] = JSON.parse(existingUsers);

    const updatedUsers = users.map((user) =>
      user.role === 'admin'
        ? {
            ...migrateUserPermissions(user),
            permissions: ADMIN_PERMISSIONS,
          }
        : migrateUserPermissions(user),
    );

    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));
    return updatedUsers;
  }

  localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify([]));
  return [];
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthSession | null>(null);

  useEffect(() => {
    if (!ADMIN_ENABLED) {
      localStorage.removeItem(STORAGE_KEYS.SESSION);
      setCurrentUser(null);
      return;
    }

    initializeUsers();

    const savedSession = localStorage.getItem(STORAGE_KEYS.SESSION);
    if (savedSession) {
      try {
        const session: AuthSession = JSON.parse(savedSession);
        const migratedUser = migrateUserPermissions({
          id: session.userId,
          username: session.username,
          role: session.role,
          displayName: session.displayName,
          permissions: session.permissions,
          isActive: true,
          createdAt: session.loginTime,
          password: '',
        });

        setCurrentUser({
          ...session,
          permissions: migratedUser.permissions,
        });
      } catch (error) {
        console.error('Ошибка загрузки сессии:', error);
        localStorage.removeItem(STORAGE_KEYS.SESSION);
      }
    }
  }, []);

  const login = (username: string, password: string): boolean => {
    if (!ADMIN_ENABLED) {
      return false;
    }

    const users: User[] = JSON.parse(localStorage.getItem(STORAGE_KEYS.USERS) || '[]');
    const user = users.find(u => u.username === username && u.password === password);

    if (!user) {
      return false;
    }

    if (!user.isActive) {
      alert('Ваш аккаунт заблокирован. Обратитесь к администратору.');
      return false;
    }

    const updatedUsers = users.map(u => 
      u.id === user.id ? { ...u, lastLogin: new Date().toISOString() } : u
    );
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(updatedUsers));

    const session: AuthSession = {
      userId: user.id,
      username: user.username,
      role: user.role,
      displayName: user.displayName,
      permissions: user.permissions,
      loginTime: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(session));
    setCurrentUser(session);
    return true;
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.SESSION);
    setCurrentUser(null);
  };

  const hasPermission = (
    resource: keyof Permission,
    action: string
  ): boolean => {
    if (!currentUser) return false;
    
    const resourcePermissions = currentUser.permissions[resource] as Record<string, boolean> | undefined;
    if (!resourcePermissions) return false;

    return resourcePermissions[action] === true;
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        user: currentUser,
        isAuthenticated: !!currentUser,
        login,
        logout,
        hasPermission
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
}
